import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from '../../util/reactIntl';
import { Button, ExternalLink, PrimaryButton } from '../../components';
import { Icons } from '../../components';
import { LinkedLogo } from '../../components';
import { usePWAInstall } from 'react-use-pwa-install';

import css from './FooterContainer.module.css';
import logo from '../../assets/onbuoy-logo-icon.png';
import Drawer from 'react-modern-drawer';

import '../../styles/reactModernDrawer.css';
import '../../styles/drawer.css';
import { BECOME_ONBUOY_EXPERT_LINK } from '../../util/constants';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const ROOT_URL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

function FooterSection({ titleId, links, target = '_blank' }) {
  const intl = useIntl();
  return (
    <div className={css.linkBlock}>
      <h2>{intl.formatMessage({ id: titleId })}</h2>
      {links.map((link, index) => (
        <a
          key={index}
          className={css.navLink}
          target={link?.target || target}
          href={link.name}
          rel="noopener noreferrer"
        >
          {intl.formatMessage({ id: link.labelId })}
        </a>
      ))}
    </div>
  );
}

function FooterContainer() {
  const intl = useIntl();
  const deferredPrompt = useRef(null);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const installPWA = usePWAInstall();

  const [isDrawerOpen, setOpenDrawer] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(window.innerWidth < MAX_MOBILE_SCREEN_WIDTH);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);

      // Detect iOS
      const isIOSDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
      setIsIOS(isIOSDevice);

      window.addEventListener('beforeinstallprompt', e => {
        e.preventDefault();
        deferredPrompt.current = e;
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleAddToHomeScreen = () => {
    // 1. Apple Devices: iOS never supports native prompts, so go straight to the manual drawer
    if (isIOS) {
      setOpenDrawer(true);
      return;
    }

    // 2. Native Chrome/Android: This is the most reliable prompt, always try it first!
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      deferredPrompt.current.userChoice.then(() => {
        deferredPrompt.current = null;
      });
      return;
    }

    // 3. Fallback Library: Try the react-use-pwa-install hook if the native one is missing
    if (typeof installPWA === 'function') {
      installPWA();
      return;
    }

    // 4. Last Resort: Nothing worked (likely because they are on Desktop or it's already installed)
    if (isMobileLayout) {
      setOpenDrawer(true); // Open mobile instructions
    } else {
      // Desktop alert (so the mobile drawer doesn't randomly pop up on a huge monitor)
      alert(
        "To install OnBuoy, look for the 'Install' icon on the right side of your browser's address bar. (If it's missing, the app might already be installed!)"
      );
    }
  };

  const companyLinks = [
    { name: `${ROOT_URL}/blog`, labelId: 'FooterContainer.link.blog', target: '_self' },
    { name: `${ROOT_URL}/locations`, labelId: 'FooterContainer.link.locations', target: '_self' },
    {
      name: `${ROOT_URL}/help/general-faqs/3/how-onbuoy-benefits-the-environment`,
      labelId: 'FooterContainer.link.environment',
    },
    { name: `${ROOT_URL}/p/become-a-host`, labelId: 'FooterContainer.link.becomeHost' },
    {
      name: `${ROOT_URL}/p/become-a-host-airbnb-vrbo`,
      labelId: 'FooterContainer.link.becomeHostAirbnbVrbo',
    },
    { name: `${BECOME_ONBUOY_EXPERT_LINK}`, labelId: 'FooterContainer.link.becomeOnBuoyExpert' },
  ];

  const communityLinks = [
    { name: `${ROOT_URL}/help`, labelId: 'FooterContainer.link.faq' },
    { name: `${ROOT_URL}/help/host-toolkit`, labelId: 'FooterContainer.link.toolkit' },
    {
      name: `${ROOT_URL}/help/general-faqs/5/what-is-onbuoy`,
      labelId: 'FooterContainer.link.whatIs',
    },
    {
      name: `${ROOT_URL}/help/general-faqs/74/contact-us`,
      labelId: 'FooterContainer.link.contact',
    },
    {
      name: `${ROOT_URL}/help/general-faqs/5/what-is-onbuoy`,
      labelId: 'FooterContainer.link.about',
    },
  ];

  const topLocationsLinks = [
    {
      name: `${ROOT_URL}/s?address=&bounds=48.88869801%2C-121.13315565%2C46.80367813%2C-124.09667354&mapSearch=true&search=`,
      labelId: 'FooterContainer.link.pugetSound',
    },
    {
      name: `${ROOT_URL}/s?address=Seattle%2C%20Washington%2C%20United%20States&bounds=47.7379309%2C-122.215854%2C47.4772268%2C-122.4628463&search=Seattle%2C%20Washington%2C%20United%20States`,
      labelId: 'FooterContainer.link.seattle',
    },
    {
      name: `${ROOT_URL}/s?address=Washington%20State%20Parks%2C%20Blake%20Island%2C%20Port%20Orchard%2C%20Washington%2098353%2C%20United%20States&bounds=47.61546456%2C-122.3722483%2C47.4594461%2C-122.59265269&mapSearch=true&search=Washington%20State%20Parks%2C%20Blake%20Island%2C%20Port%20Orchard%2C%20Washington%2098353%2C%20United%20States`,
      labelId: 'FooterContainer.link.blakeIsland',
    },
  ];

  const MobileDrawer = (
    <Drawer
      open={isDrawerOpen}
      onClose={() => {
        setOpenDrawer(false);
      }}
      direction="bottom"
      className={css.filterDrawer}
      lockBackgroundScroll
      overlayOpacity="0.45"
      overlayColor="#000000"
    >
      <div className={css.drawerHead}>
        Intall the app{' '}
        <span
          className={css.close}
          onClick={() => {
            setOpenDrawer(false);
          }}
        >
          <Icons name="close" />
        </span>
      </div>

      <div className={css.drawerBody}>
        <div className={css.onbuoySec}>
          <div className={css.onbuoyLogo}>
            <img src={logo} alt="" />
          </div>
          <div className={css.onbuoyInfo}>
            <h2>OnBuoy</h2>
            <p>www.onbuoy.com</p>
          </div>
        </div>
        <div className={css.otherInfo}>
          <div className={css.info}>
            1. Tap on the
            <span className={css.upload}>
              <Icons name="uploadSquare" />
            </span>
            in the browser toolbar
          </div>
          <div className={css.info}>
            2. Scroll down and select <span className={css.highlight}>Add to Home Screen</span>
          </div>
          <div className={css.info}>
            3. Look for the
            <span className={css.iconlogo}>
              <img src={logo} alt="" />
            </span>
            icon on your home screen
          </div>
        </div>
      </div>
    </Drawer>
  );

  return (
    <div className={css.footer}>
      <div className={css.contentWidth}>
        <div className={css.footerTop}>
          <FooterSection
            titleId="FooterContainer.title.explore"
            target="_self"
            links={companyLinks}
          />
          <FooterSection
            titleId="FooterContainer.title.help"
            target="_self"
            links={communityLinks}
          />
          {!isMobileLayout && (
            <FooterSection titleId="FooterContainer.title.topLocations" links={topLocationsLinks} />
          )}

          <div className={css.linkBlock}>
            <h2>{intl.formatMessage({ id: 'FooterContainer.connectWithUs' })}</h2>
            <div className={css.socialLinks}>
              <ExternalLink href="https://www.facebook.com/OnBuoySPC" className={css.navLink}>
                <Icons name="facebook" />
              </ExternalLink>
              <ExternalLink href="https://www.instagram.com/onbuoyspc" className={css.navLink}>
                <Icons name="instagram" />
              </ExternalLink>
              <ExternalLink href="https://www.linkedin.com/company/onbuoy/" className={css.navLink}>
                <Icons name="linkedin" />
              </ExternalLink>
              <ExternalLink href="https://www.youtube.com/@OnBuoy" className={css.navLink}>
                <Icons name="youtube" />
              </ExternalLink>
            </div>{' '}
            <PrimaryButton onClick={handleAddToHomeScreen} className={css.addToHomeBtn}>
              <Icons name="downloadApp" /> Add OnBuoy to Home Screen
            </PrimaryButton>
          </div>
        </div>

        <div className={css.footerBottom}>
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
          />
          <div className={css.rightLinks}>
            <p>{intl.formatMessage({ id: 'FooterContainer.ARR' })}</p>
            <a className={css.navLink} href="/privacy-policy">
              {intl.formatMessage({ id: 'FooterContainer.privacyPolicy' })}
            </a>
            <a className={css.navLink} href="/terms-of-service">
              {intl.formatMessage({ id: 'FooterContainer.termsOfUse' })}
            </a>
          </div>
        </div>
      </div>
      {MobileDrawer}
    </div>
  );
}

export default FooterContainer;
